<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, reactive, ref } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import {
  CreateProduct,
  Mutation,
  PermissionOrganization,
  ProductViewArchiveMutation,
  ProductViewArchiveMutationVariables,
  ProductViewCreateMutation,
  ProductViewCreateMutationVariables,
  ProductViewDataQuery,
  ProductViewUnarchiveMutation,
  ProductViewUnarchiveMutationVariables,
  Query,
  UpdateProduct,
  UpdateSite,
} from '@/generated/graphql'
import gql from 'graphql-tag'
import useVuelidate from '@vuelidate/core'
import { currentOrganization } from '@/app'
import TextField from '@/components/input/TextField.vue'
import { abbreviation, normalDescription, normalName, required, unique } from '@/validation'
import TextAreaField from '@/components/input/TextAreaField.vue'
import { useRouter } from 'vue-router'
import ImagePicker from '@/components/input/ImagePicker.vue'
import { ComponentExposed } from 'vue-component-type-helpers'
import UpsertDialog from '@/components/dialogs/UpsertDialog.vue'
import DeleteDialogNext from '@/components/dialogs/AsyncDeleteDialog.vue'
import AsyncDeleteDialog from '@/components/dialogs/AsyncDeleteDialog.vue'
import AsyncConfirmDialog from '@/components/dialogs/AsyncConfirmDialog.vue'
import ArchivedChip from '@/components/archive/ArchivedChip.vue'
import useAuthorization from '@/composables/useAuthorization'
import { mockUpdateProduct } from '@/generated/graphql-mocks'

type LocalProduct = ProductViewDataQuery['product']['products'][0]
type Model = Partial<UpdateProduct>
const modelKeys = Object.keys(mockUpdateProduct())

const { t } = useI18n()
const { hasOrgAuthz } = useAuthorization()

const fetchQuery = useQuery<Query>(
  gql`
    query ProductViewData {
      product {
        products {
          id
          name
          abbreviation
          description
          imageUploadId
          archived
          imageUpload {
            id
            imageUrl(options: { width: 500, height: 300 })
          }
        }
      }
    }
  `,
  () => ({
    org: currentOrganization.value, // Here to refresh the query when the org changes
  }),
)
const items = computed(() => fetchQuery.result.value?.product.products || [])

type Tab = 'active' | 'archived'
const tab = ref<Tab>('active')
const visibleItems = computed(() =>
  items.value.filter((p) => p.archived == (tab.value == 'archived')),
)

const model = reactive<Model>({})

const takenAbbreviations = computed(() =>
  items.value.filter((i) => i.id != model.id).map((i) => i.abbreviation),
)
const validation = useVuelidate<Model>(
  {
    name: { required, normalName },
    abbreviation: { required, abbreviation, unique: unique(takenAbbreviations) },
    description: { normalDescription },
  },
  model,
)

const createMutation = useMutation<ProductViewCreateMutation, ProductViewCreateMutationVariables>(
  gql`
    mutation ProductViewCreate($command: CreateProduct!) {
      product {
        createProduct(command: $command) {
          id
        }
      }
    }
  `,
)
const createDialog = ref<ComponentExposed<typeof UpsertDialog<() => Promise<unknown>>>>()
const doCreate = async () => {
  modelKeys.forEach((k) => (model[k as keyof Model] = undefined))
  await createDialog.value?.open(() =>
    createMutation.mutate({ command: model as CreateProduct }).then(() => fetchQuery.refetch()),
  )
}

const updateMutation = useMutation<Mutation>(gql`
  mutation ProductViewUpdate($command: UpdateProduct!) {
    product {
      updateProduct(command: $command) {
        id
      }
    }
  }
`)
const updateDialog = ref<ComponentExposed<typeof UpsertDialog<() => Promise<unknown>>>>()
const doUpdate = async (item: LocalProduct) => {
  modelKeys.forEach((k) => (model[k as keyof Model] = item[k as keyof Model]))
  await updateDialog.value?.open(() =>
    updateMutation.mutate({ command: model as UpdateSite }).then(() => fetchQuery.refetch()),
  )
}

const deleteMutation = useMutation<Mutation>(gql`
  mutation ProductViewDelete($id: ID!) {
    product {
      deleteProduct(id: $id)
    }
  }
`)
const deleteDialog = ref<ComponentExposed<typeof DeleteDialogNext>>()
const doDelete = async (item: LocalProduct) => {
  await deleteDialog.value?.open(item.name, () =>
    deleteMutation.mutate({ id: item.id }).then(() => fetchQuery.refetch()),
  )
}

const archiveMutation = useMutation<
  ProductViewArchiveMutation,
  ProductViewArchiveMutationVariables
>(gql`
  mutation ProductViewArchive($id: ID!) {
    product {
      archiveProduct(id: $id) {
        id
      }
    }
  }
`)
const archiveDialog = ref<ComponentExposed<typeof AsyncConfirmDialog<LocalProduct>>>()
const doArchive = async (item: LocalProduct) => {
  await archiveDialog.value?.open(
    () => archiveMutation.mutate({ id: item.id }).then(() => fetchQuery.refetch()),
    item,
  )
}

const unarchiveMutation = useMutation<
  ProductViewUnarchiveMutation,
  ProductViewUnarchiveMutationVariables
>(gql`
  mutation ProductViewUnarchive($id: ID!) {
    product {
      unarchiveProduct(id: $id) {
        id
      }
    }
  }
`)
const unarchiveDialog = ref<ComponentExposed<typeof AsyncConfirmDialog<LocalProduct>>>()
const doUnarchive = async (item: LocalProduct) => {
  await unarchiveDialog.value?.open(
    () => unarchiveMutation.mutate({ id: item.id }).then(() => fetchQuery.refetch()),
    item,
  )
}

const router = useRouter()
function goToConfigurations(productId: string) {
  router.push({
    name: 'productConfigurations',
    params: { productId },
  })
}
</script>

<template>
  <h1 class="d-flex align-center">
    {{ t('entity.product.plural') }}
    <v-spacer />
    <v-btn
      v-if="hasOrgAuthz(PermissionOrganization.OrgProductRead)"
      :to="{ name: 'library' }"
      icon="library_books"
      color="primary"
      class="mr-2"
      :title="t('view.organization.library.title')"
    />
    <v-btn icon="add" color="primary" @click="doCreate()" />
  </h1>

  <v-tabs v-model="tab" class="mb-4">
    <v-tab value="active">{{ t('component.archive.tabActive') }}</v-tab>
    <v-tab value="archived">{{ t('component.archive.tabArchived') }}</v-tab>
  </v-tabs>

  <v-alert v-if="visibleItems.length == 0 && !fetchQuery.loading.value" type="info">
    {{
      tab == 'archived' ? t('component.archive.noArchivedItems') : t('component.dataTable.noData')
    }}
  </v-alert>
  <v-row>
    <v-col v-for="p in visibleItems" :key="p.id" xxl="2" xl="3" lg="3" sm="4" xs="6">
      <v-card :class="{ 'archived-card': p.archived }">
        <router-link :to="{ name: 'productConfigurations', params: { productId: p.id } }">
          <v-img v-if="p.imageUpload?.imageUrl" :src="p.imageUpload.imageUrl" height="175px" />
        </router-link>
        <v-card-title>
          <router-link
            :to="{ name: 'productConfigurations', params: { productId: p.id } }"
            style="color: #000"
          >
            {{ p.name }}
          </router-link>
        </v-card-title>
        <v-card-text>
          <div v-if="p.archived" class="mb-2"><archived-chip /></div>
          {{ p.description }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn density="compact" size="small" icon="settings" @click="goToConfigurations(p.id)" />
          <template v-if="hasOrgAuthz(PermissionOrganization.OrgProductWrite)">
            <v-btn
              v-if="p.archived"
              density="compact"
              size="small"
              icon="unarchive"
              :title="t('button.unarchive')"
              @click="doUnarchive(p)"
            />
            <template v-else>
              <v-btn
                density="compact"
                size="small"
                icon="edit"
                :title="t('button.edit')"
                @click="doUpdate(p)"
              />
              <v-btn
                density="compact"
                size="small"
                icon="archive"
                :title="t('button.archive')"
                @click="doArchive(p)"
              />
            </template>
          </template>
          <v-btn
            v-if="hasOrgAuthz(PermissionOrganization.OrgProductDelete)"
            density="compact"
            size="small"
            icon="delete"
            @click="doDelete(p)"
          />
        </v-card-actions>
      </v-card>
    </v-col>
  </v-row>

  <async-delete-dialog ref="deleteDialog" :warning="t('entity.product.deleteWarning')" hindrance />

  <async-confirm-dialog ref="archiveDialog">
    <template #default="{ item }">
      {{ t('component.archive.archiveProductMessage', { name: (item as LocalProduct)?.name }) }}
    </template>
  </async-confirm-dialog>

  <async-confirm-dialog ref="unarchiveDialog">
    <template #default="{ item }">
      {{ t('component.archive.unarchiveProductMessage', { name: (item as LocalProduct)?.name }) }}
    </template>
  </async-confirm-dialog>

  <upsert-dialog
    ref="createDialog"
    type="create"
    :entity="t('entity.product.singular')"
    :validation="validation"
  >
    <text-field
      v-model="model.name"
      :label="t('entity.product.field.name')"
      required
      :validation="validation.name"
    />
    <text-field
      v-model="model.abbreviation"
      :label="t('entity.product.field.abbreviation')"
      required
      :validation="validation.abbreviation"
    />
    <text-area-field
      v-model="model.description"
      :label="t('entity.product.field.description')"
      required
      :validation="validation.description"
    />
    <image-picker v-model="model.imageUploadId" :label="t('entity.product.field.image')" />
  </upsert-dialog>

  <upsert-dialog
    ref="updateDialog"
    type="update"
    :entity="t('entity.product.singular')"
    :validation="validation"
  >
    <text-field
      v-model="model.name"
      required
      :label="t('entity.product.field.name')"
      :validation="validation.name"
    />
    <text-field
      v-model="model.abbreviation"
      :label="t('entity.product.field.abbreviation')"
      required
      :validation="validation.abbreviation"
    />
    <text-area-field
      v-model="model.description"
      :label="t('entity.product.field.description')"
      :validation="validation.description"
    />
    <image-picker v-model="model.imageUploadId" :label="t('entity.product.field.image')" />
  </upsert-dialog>
</template>

<style scoped lang="scss">
.archived-card :deep(.v-img) {
  filter: grayscale(1);
  opacity: 0.6;
}
</style>
